import React from "react";

import "./ga4Analytics.css";

/*
 * Shared GA4 presentation modules.
 *
 * These were built inside Campaign Analysis (SherlocReports.js) and are the
 * REAL, already-shipped GA4 experience — the daily model, the segmented
 * traffic line, the device/demographic breakdowns and Google's privacy
 * thresholding. They are extracted here rather than reimplemented so the
 * Campaign Analysis detail surface and the /ga4-analytics page render the same
 * modules from one definition and cannot drift apart.
 *
 * Moved verbatim: the only edits are `export`, the de-indent, and the
 * formatter names. The honesty contracts are therefore carried over exactly:
 *   - a day GA4 never reported is ABSENT, drawn as a gap, never as zero;
 *   - a genuinely reported 0 is kept, because it is an observation;
 *   - dates are bucketed in the GA4 PROPERTY's time zone, which the series
 *     reports rather than the client assuming;
 *   - age/gender rows withheld by Google's thresholding are stated as withheld,
 *     never replaced with an invented chart.
 */

const toGaNumber = (value) => {
  if (value == null) return NaN;
  if (typeof value === "number") return value;
  const text = String(value).trim();
  if (!text) return NaN;
  const cleaned = text.replace(/[\s]/g, "").replace(/\$/g, "").replace(/,/g, "").replace(/%/g, "");
  const numeric = Number(cleaned);
  return Number.isFinite(numeric) ? numeric : NaN;
};

export const formatGaNumber = (v) => {
  if (v == null || v === "") return "0";
  const num = typeof v === "number" ? v : toGaNumber(v);
  if (Number.isNaN(num)) return "0";
  return Number(num).toLocaleString("en-US");
};

export const formatGaPercent = (v) => {
  const num = typeof v === "number" ? v : toGaNumber(v);
  if (v == null || Number.isNaN(num)) return "No Data";
  return `${Number(num).toFixed(2)}%`;
};

export const formatGaDuration = (secondsLike) => {
  const total = toGaNumber(secondsLike);
  if (!Number.isFinite(total) || total < 0) return "No Data";
  const totalSeconds = Math.round(total);
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  if (mins <= 0) return `${secs}s`;
  return `${mins}m ${String(secs).padStart(2, "0")}s`;
};


/*
 * Copied verbatim from SherlocReports so this module has no dependency back on
 * the page it was extracted from. They are pure value-shaping helpers.
 */
const toNumber = (v) => {
  if (v == null) return NaN;
  if (typeof v === "number") return v;
  const s = String(v).trim();
  if (!s) return NaN;
  const cleaned = s.replace(/[\s]/g, "").replace(/\$/g, "").replace(/,/g, "").replace(/%/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : NaN;
};

const computeRatePercent = (numerator, denominator) => {
  const num = toNumber(numerator ?? 0);
  const den = toNumber(denominator ?? 0);
  if (!Number.isFinite(num) || !Number.isFinite(den) || den <= 0) return null;
  const pct = (num / den) * 100;
  return Number.isFinite(pct) ? pct : null;
};

const isPlainObject = (v) => v && typeof v === "object" && !Array.isArray(v);

const toNumSafe = (v) => {
  const n = toNumber(v);
  return Number.isFinite(n) ? n : 0;
};

const pickFirst = (...vals) => {
  for (const v of vals) {
    if (v == null) continue;
    if (typeof v === "string" && !v.trim()) continue;
    return v;
  }
  return null;
};

const normalizeGoogleAnalyticsDimensionRows = (
  input,
  { dimensionKeys = [], defaultMetric = "users" } = {}
) => {
  const sourceRows = Array.isArray(input)
    ? input
    : Array.isArray(input?.rows)
    ? input.rows
    : Array.isArray(input?.items)
    ? input.items
    : Array.isArray(input?.data)
    ? input.data
    : isPlainObject(input)
    ? Object.entries(input).map(([label, value]) =>
        isPlainObject(value) ? { label, ...value } : { label, [defaultMetric]: value }
      )
    : [];

  return sourceRows
    .map((row) => {
      if (!isPlainObject(row)) return null;
      const label = String(
        pickFirst(...dimensionKeys.map((key) => row?.[key]), row?.label, row?.name, row?.dimension)
          ?? ""
      ).trim();
      if (!label) return null;

      const users = toNumSafe(
        pickFirst(row.users, row.totalUsers, row.activeUsers, row.active_users, row.value)
      );
      const sessions = toNumSafe(pickFirst(row.sessions, row.sessionCount));
      const engagedSessions = toNumSafe(
        pickFirst(row.engagedSessions, row.engaged_sessions)
      );
      const eventCount = toNumSafe(pickFirst(row.eventCount, row.event_count, row.events));
      const avgSessionDuration = toNumSafe(
        pickFirst(
          row.avgSessionDuration,
          row.averageSessionDuration,
          row.avg_session_duration,
          row.average_session_duration
        )
      );
      const providedEngagementRate = toNumber(
        pickFirst(row.engagementRate, row.engagement_rate)
      );
      const engagementRate =
        Number.isFinite(providedEngagementRate) && providedEngagementRate > 1
          ? providedEngagementRate
          : Number.isFinite(providedEngagementRate) && providedEngagementRate >= 0
          ? providedEngagementRate * 100
          : computeRatePercent(engagedSessions, sessions);
      const eventsPerUser = users > 0 && eventCount > 0 ? eventCount / users : null;
      const sessionsPerUser = users > 0 && sessions > 0 ? sessions / users : null;

      const hasMetric = [
        users,
        sessions,
        engagedSessions,
        eventCount,
        avgSessionDuration,
        engagementRate,
      ].some((value) => Number.isFinite(value) && value > 0);

      if (!hasMetric) return null;

      return {
        label,
        users,
        sessions,
        engagedSessions,
        engagementRate,
        eventCount,
        avgSessionDuration,
        eventsPerUser,
        sessionsPerUser,
      };
    })
    .filter(Boolean);
};

export const GA_DIMENSION_COLORS = [
  "#3b73e6",
  "#55b8ca",
  "#de3398",
  "#e7762f",
  "#f4b33b",
  "#8bbd45",
  "#8a64dc",
];

/* ------------------------------------------------------------------ */
/* Segment aggregation semantics                                       */
/*                                                                     */
/* A donut asserts a sentence: "these slices are parts of one whole."   */
/* That sentence is only true of an ADDITIVE quantity. Engagement Rate  */
/* by Device shipped to production summing 16.31% + 18.89% + 8.86% into */
/* a 44.06% centre — a number that describes nothing — and drew wedges  */
/* that were each device's share of that sum of rates, which is not a   */
/* composition of anything. Rates, averages and distinct counts are not */
/* additive, and being a finite number in a segment row does not make   */
/* them so.                                                            */
/*                                                                     */
/* Aggregation is therefore DECLARED per metric here, never inferred at */
/* the renderer and never guessed from a "%" in a formatted string. A   */
/* metric nobody registered is `unsupported`: it degrades to comparison */
/* bars rather than being summed, because a renderer quietly assuming   */
/* addition on an unclassified metric is the exact defect being fixed.  */
/*                                                                     */
/*   additive  — the segments genuinely add up (Sessions, Event Count). */
/*               centre = the window total, wedge = the segment value.  */
/*   weighted  — a rate/ratio/average. The centre is GA4's own window   */
/*               value; wedges come from `compositionKey`, the metric   */
/*               that IS a part-to-whole (usually Sessions).            */
/*   distinct  — a de-duplicated count (Users). Segments legitimately   */
/*               sum ABOVE the window total, so the sum is never the    */
/*               centre; wedges are still a real composition of the     */
/*               segment-attributed counts, which is disclosed.         */
/* ------------------------------------------------------------------ */

export const GA_SEGMENT_AGGREGATION = {
  ADDITIVE: "additive",
  WEIGHTED: "weighted",
  DISTINCT: "distinct",
  UNSUPPORTED: "unsupported",
};

/*
 * GA4 reports rates as a 0–1 ratio, while the canonical reporting contract
 * normalises breakdown rows onto a percent axis before they reach a client.
 * Both shapes arrive here, so one rule resolves them — the same rule
 * `buildGa4MeasurementRaw` and `normalizeGoogleAnalyticsDimensionRows`
 * already apply, so the donut centre and the KPI card cannot disagree.
 */
export const normalizeGaRatePercent = (value) => {
  const numeric = toNumber(value);
  if (!Number.isFinite(numeric) || numeric < 0) return null;
  return numeric > 1 ? numeric : numeric * 100;
};

const identityMetric = (value) => {
  const numeric = toNumber(value);
  return Number.isFinite(numeric) ? numeric : null;
};

export const GA_SEGMENT_METRIC_SEMANTICS = {
  /* --- additive counts ------------------------------------------- */
  sessions: {
    label: "Sessions",
    aggregation: GA_SEGMENT_AGGREGATION.ADDITIVE,
    windowKeys: ["sessions", "sessionCount", "session_count"],
  },
  engagedSessions: {
    label: "Engaged Sessions",
    aggregation: GA_SEGMENT_AGGREGATION.ADDITIVE,
    windowKeys: ["engagedSessions", "engaged_sessions"],
  },
  eventCount: {
    label: "Event Count",
    aggregation: GA_SEGMENT_AGGREGATION.ADDITIVE,
    windowKeys: ["eventCount", "event_count"],
  },
  screenPageViews: {
    label: "Page Views",
    aggregation: GA_SEGMENT_AGGREGATION.ADDITIVE,
    windowKeys: ["screenPageViews", "screen_page_views", "pageViews", "page_views"],
  },
  keyEvents: {
    label: "Key Events",
    aggregation: GA_SEGMENT_AGGREGATION.ADDITIVE,
    windowKeys: ["keyEvents", "key_events", "conversions"],
  },
  totalRevenue: {
    label: "Revenue",
    aggregation: GA_SEGMENT_AGGREGATION.ADDITIVE,
    windowKeys: ["totalRevenue", "total_revenue", "revenue"],
  },

  /* --- distinct counts ------------------------------------------- */
  /*
   * GA4 de-duplicates a person once per DIMENSION VALUE, so someone who
   * browsed on a phone and a laptop is one window user and two device-row
   * users. Summing the rows overstates reach; GA4's own window figure is
   * the only correct aggregate.
   */
  users: {
    label: "Users",
    aggregation: GA_SEGMENT_AGGREGATION.DISTINCT,
    windowKeys: ["users", "totalUsers", "total_users", "activeUsers", "active_users"],
  },
  newUsers: {
    label: "New Users",
    aggregation: GA_SEGMENT_AGGREGATION.DISTINCT,
    windowKeys: ["newUsers", "new_users"],
  },

  /* --- rates, ratios and averages -------------------------------- */
  engagementRate: {
    label: "Engagement Rate",
    aggregation: GA_SEGMENT_AGGREGATION.WEIGHTED,
    segmentNoun: "rates",
    compositionKey: "sessions",
    compositionLabel: "Sessions",
    windowKeys: ["engagementRate", "engagement_rate"],
    scale: normalizeGaRatePercent,
    /*
     * GA4 defines engagementRate as engagedSessions / sessions, so when the
     * rows carry both the aggregate is recoverable exactly rather than as a
     * weighted average of already-rounded rates.
     */
    ratio: { numeratorKey: "engagedSessions", denominatorKey: "sessions", asPercent: true },
  },
  bounceRate: {
    label: "Bounce Rate",
    aggregation: GA_SEGMENT_AGGREGATION.WEIGHTED,
    segmentNoun: "rates",
    compositionKey: "sessions",
    compositionLabel: "Sessions",
    windowKeys: ["bounceRate", "bounce_rate"],
    scale: normalizeGaRatePercent,
  },
  conversionRate: {
    label: "Conversion Rate",
    aggregation: GA_SEGMENT_AGGREGATION.WEIGHTED,
    segmentNoun: "rates",
    compositionKey: "sessions",
    compositionLabel: "Sessions",
    windowKeys: ["conversionRate", "conversion_rate", "sessionConversionRate"],
    scale: normalizeGaRatePercent,
  },
  ctr: {
    label: "CTR",
    aggregation: GA_SEGMENT_AGGREGATION.WEIGHTED,
    segmentNoun: "rates",
    compositionKey: "impressions",
    compositionLabel: "Impressions",
    windowKeys: ["ctr"],
    scale: normalizeGaRatePercent,
    ratio: { numeratorKey: "clicks", denominatorKey: "impressions", asPercent: true },
  },
  avgSessionDuration: {
    label: "Avg Session Duration",
    aggregation: GA_SEGMENT_AGGREGATION.WEIGHTED,
    segmentNoun: "averages",
    compositionKey: "sessions",
    compositionLabel: "Sessions",
    windowKeys: [
      "avgSessionDuration",
      "averageSessionDuration",
      "avg_session_duration",
      "average_session_duration",
    ],
    scale: identityMetric,
  },
  averageEngagementTime: {
    label: "Avg Engagement Time",
    aggregation: GA_SEGMENT_AGGREGATION.WEIGHTED,
    segmentNoun: "averages",
    compositionKey: "sessions",
    compositionLabel: "Sessions",
    windowKeys: ["averageEngagementTime", "average_engagement_time"],
    scale: identityMetric,
  },
  eventsPerUser: {
    label: "Events per User",
    aggregation: GA_SEGMENT_AGGREGATION.WEIGHTED,
    segmentNoun: "ratios",
    compositionKey: "users",
    compositionLabel: "Users",
    windowKeys: ["eventsPerUser", "events_per_user"],
    scale: identityMetric,
    ratio: { numeratorKey: "eventCount", denominatorKey: "users", asPercent: false },
  },
  sessionsPerUser: {
    label: "Sessions per User",
    aggregation: GA_SEGMENT_AGGREGATION.WEIGHTED,
    segmentNoun: "ratios",
    compositionKey: "users",
    compositionLabel: "Users",
    windowKeys: ["sessionsPerUser", "sessions_per_user"],
    scale: identityMetric,
    ratio: { numeratorKey: "sessions", denominatorKey: "users", asPercent: false },
  },
};

export const resolveGaSegmentMetricSemantics = (metricKey) => {
  const declared = GA_SEGMENT_METRIC_SEMANTICS[metricKey];
  if (declared) {
    return {
      compositionKey: declared.compositionKey || metricKey,
      compositionLabel: declared.compositionLabel || declared.label,
      segmentNoun: declared.segmentNoun || "values",
      scale: declared.scale || identityMetric,
      ratio: null,
      windowKeys: [],
      ...declared,
    };
  }
  /*
   * Unregistered metrics are NOT assumed additive. The whole defect was a
   * renderer treating an unclassified numeric column as a quantity that
   * adds up, so the safe default is "this cannot be composed" — the card
   * degrades to bars and no total is invented.
   */
  return {
    label: metricKey,
    aggregation: GA_SEGMENT_AGGREGATION.UNSUPPORTED,
    segmentNoun: "values",
    compositionKey: metricKey,
    compositionLabel: metricKey,
    scale: identityMetric,
    ratio: null,
    windowKeys: [],
  };
};

/*
 * GA4's authoritative window figures, wherever the governed wrapper put them.
 * These are the canonical values the KPI cards already print; the donut reads
 * the same numbers rather than re-deriving a window aggregate from segment
 * rows, which may be thresholded, rounded, or missing an "(other)" bucket.
 */
export const resolveGaWindowMetrics = (channelData = {}) => {
  const sources = [
    channelData,
    channelData?.overview,
    channelData?.windowMetrics,
    channelData?.summary,
    channelData?.summary?.overview,
    channelData?.totals,
  ].filter(isPlainObject);

  const readWindowMetric = (metricKey) => {
    const semantics = resolveGaSegmentMetricSemantics(metricKey);
    for (const source of sources) {
      for (const key of semantics.windowKeys) {
        if (source?.[key] == null || source?.[key] === "") continue;
        const scaled = semantics.scale(source[key]);
        if (scaled != null && Number.isFinite(scaled)) return scaled;
      }
    }
    return null;
  };

  const metrics = {};
  Object.keys(GA_SEGMENT_METRIC_SEMANTICS).forEach((metricKey) => {
    metrics[metricKey] = readWindowMetric(metricKey);
  });

  /*
   * A rate GA4 did not report at window level is still recoverable from the
   * window numerator and denominator it DID report. That is GA4's own
   * arithmetic on GA4's own totals, not a recomposition of segment rows.
   */
  if (metrics.engagementRate == null && metrics.sessions > 0 && metrics.engagedSessions != null) {
    metrics.engagementRate = (metrics.engagedSessions / metrics.sessions) * 100;
  }

  return metrics;
};


/* ------------------------------------------------------------------ */
/* Traffic Over Time — daily GA4 observations                          */
/* ------------------------------------------------------------------ */

/*
 * Metrics the governed daily model exposes (backend #161). All four are counts
 * and share one axis, so a selector can swap between them without the scale
 * changing meaning. Ratio and duration metrics are deliberately absent — a rate
 * on a count axis misreads by orders of magnitude.
 */
export const GA_DAILY_METRIC_OPTIONS = [
  { key: "sessions", label: "Site Visits · GA4", short: "Sessions" },
  { key: "totalUsers", label: "Users · GA4", short: "Users" },
  { key: "newUsers", label: "New Users · GA4", short: "New Users" },
  { key: "engagedSessions", label: "Engaged Sessions · GA4", short: "Engaged" },
];

export function resolveGaDailyMetric(key) {
  return (
    GA_DAILY_METRIC_OPTIONS.find((option) => option.key === key) ||
    GA_DAILY_METRIC_OPTIONS[0]
  );
}

/*
 * A day GA4 never reported is ABSENT from the series, and the line has to show
 * that as a hole. This builds one subpath per run of observations rather than
 * skipping nulls and continuing, which would bridge a gap with a straight
 * segment and read as interpolated data. Same contract as the Quantum overlay.
 */
export function buildGaSegmentedPath(points) {
  let d = "";
  let pendingMove = true;
  points.forEach((point) => {
    if (point == null || point.value == null) {
      pendingMove = true;
      return;
    }
    d += `${pendingMove ? "M" : "L"} ${point.x.toFixed(1)} ${point.y.toFixed(1)} `;
    pendingMove = false;
  });
  return d.trim();
}

export function GaTrafficOverTimeChart({ series, metricKey, onMetricChange, loading, error }) {
  const metric = resolveGaDailyMetric(metricKey);
  const days = Array.isArray(series?.days) ? series.days : [];
  const observed = days.filter((day) => day && day.value != null);

  const width = 720;
  const height = 190;
  const marginLeft = 52;
  const marginRight = 16;
  const marginTop = 14;
  const marginBottom = 26;
  const plotWidth = Math.max(40, width - marginLeft - marginRight);
  const plotHeight = Math.max(40, height - marginTop - marginBottom);

  const maxValue = observed.length ? Math.max(...observed.map((day) => day.value)) : 0;
  const yMax = maxValue > 0 ? maxValue * 1.15 : 1;
  const slot = days.length > 1 ? plotWidth / (days.length - 1) : plotWidth;
  const points = days.map((day, index) => ({
    date: day?.date || "",
    value: day?.value == null ? null : day.value,
    x: marginLeft + (days.length > 1 ? index * slot : plotWidth / 2),
    y: marginTop + plotHeight - ((day?.value || 0) / yMax) * plotHeight,
  }));

  return (
    <section className="sherlocreports-ga-dashboard-section sherlocreports-ga-traffic-section">
      <div className="sherlocreports-ga-traffic-header">
        <h3>Traffic Over Time</h3>
        <div className="sherlocreports-ga-traffic-controls" role="group" aria-label="Traffic metric">
          {GA_DAILY_METRIC_OPTIONS.map((option) => (
            <button
              key={option.key}
              type="button"
              className={
                option.key === metric.key
                  ? "sherlocreports-ga-traffic-chip sherlocreports-ga-traffic-chip--active"
                  : "sherlocreports-ga-traffic-chip"
              }
              aria-pressed={option.key === metric.key}
              onClick={() => onMetricChange(option.key)}
            >
              {option.short}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="sherlocreports-ga-demographic-helper">Loading daily observations…</p>
      ) : error ? (
        <p className="sherlocreports-ga-demographic-helper">{error}</p>
      ) : !observed.length ? (
        <p className="sherlocreports-ga-demographic-helper">
          GA4 reported no daily observations in this window. Nothing is drawn rather than a
          zero line, which would be a floor that never happened.
        </p>
      ) : (
        <>
          <svg
            className="sherlocreports-ga-traffic-svg"
            viewBox={`0 0 ${width} ${height}`}
            role="img"
            aria-label={`${metric.label} by day. ${observed.length} observed ${
              observed.length === 1 ? "day" : "days"
            }.`}
          >
            {[0, 0.5, 1].map((fraction) => {
              const y = marginTop + plotHeight - fraction * plotHeight;
              return (
                <g key={`ga-grid-${fraction}`}>
                  <line
                    x1={marginLeft}
                    x2={width - marginRight}
                    y1={y}
                    y2={y}
                    className="sherlocreports-ga-traffic-gridline"
                  />
                  <text
                    x={marginLeft - 8}
                    y={y + 4}
                    className="sherlocreports-ga-traffic-axis sherlocreports-numeric"
                    textAnchor="end"
                  >
                    {formatGaNumber(Math.round(fraction * yMax))}
                  </text>
                </g>
              );
            })}

            <path className="sherlocreports-ga-traffic-line" d={buildGaSegmentedPath(points)} />

            {points.map((point) =>
              point.value == null ? null : (
                <circle
                  key={`ga-dot-${point.date}`}
                  className="sherlocreports-ga-traffic-dot"
                  cx={point.x}
                  cy={point.y}
                  r={3}
                >
                  <title>{`${point.date} · ${metric.label}: ${formatGaNumber(point.value)}`}</title>
                </circle>
              )
            )}
          </svg>

          <div className="sherlocreports-ga-traffic-readout">
            {points
              .filter((point) => point.value != null)
              .map((point) => (
                <span className="sherlocreports-ga-traffic-readout-item" key={`ga-read-${point.date}`}>
                  <span className="sherlocreports-ga-traffic-readout-date">{point.date}</span>
                  <span className="sherlocreports-ga-traffic-readout-value sherlocreports-numeric">
                    {formatGaNumber(point.value)}
                  </span>
                </span>
              ))}
          </div>

          <p className="sherlocreports-ga-demographic-helper">
            {observed.length} observed {observed.length === 1 ? "day" : "days"} of {days.length}
            {series?.timeZone ? ` · dates in ${series.timeZone} (GA4 property time zone)` : ""}
            . Days GA4 did not report are left out rather than drawn as zero.
          </p>
        </>
      )}
    </section>
  );
}

export const buildGoogleAnalyticsAudienceDashboard = (channelData = {}) => {
  const ageRows = Array.isArray(channelData?.age_breakdown)
    ? channelData.age_breakdown
    : normalizeGoogleAnalyticsDimensionRows(
        pickFirst(channelData?.ageBreakdown, channelData?.age_breakdown),
        {
          dimensionKeys: ["ageRange", "age_range", "ageGroup", "age_group", "userAgeBracket", "age"],
        }
      );
  const genderRows = Array.isArray(channelData?.gender_breakdown)
    ? channelData.gender_breakdown
    : normalizeGoogleAnalyticsDimensionRows(
        pickFirst(channelData?.genderBreakdown, channelData?.gender_breakdown),
        { dimensionKeys: ["gender", "userGender"] }
      );
  const deviceRows = Array.isArray(channelData?.device_breakdown)
    ? channelData.device_breakdown
    : normalizeGoogleAnalyticsDimensionRows(
        pickFirst(channelData?.deviceMix, channelData?.device_mix, channelData?.deviceBreakdown),
        { dimensionKeys: ["deviceCategory", "device_category", "device", "platform"] }
      );

  const ageChartSpecs = [
    { key: "users", title: "Users by Age Range", metricKey: "users", formatter: formatGaNumber },
    { key: "sessions", title: "Sessions by Age Range", metricKey: "sessions", formatter: formatGaNumber },
    {
      key: "engagedSessions",
      title: "Engaged Sessions by Age Range",
      metricKey: "engagedSessions",
      formatter: formatGaNumber,
    },
    {
      key: "engagementRate",
      title: "Engagement Rate by Age Range",
      metricKey: "engagementRate",
      formatter: formatGaPercent,
    },
  ].filter((spec) =>
    ageRows.some((row) => Number.isFinite(row?.[spec.metricKey]) && row[spec.metricKey] > 0)
  );

  const genderChartSpecs = [
    { key: "genderUsers", title: "Users by Gender", rows: genderRows, metricKey: "users", formatter: formatGaNumber },
    { key: "genderSessions", title: "Sessions by Gender", rows: genderRows, metricKey: "sessions", formatter: formatGaNumber },
    {
      key: "genderEngagement",
      title: "Engagement Rate by Gender",
      rows: genderRows,
      metricKey: "engagementRate",
      formatter: formatGaPercent,
    },
  ].filter((spec) =>
    spec.rows.some((row) => Number.isFinite(row?.[spec.metricKey]) && row[spec.metricKey] > 0)
  );

  const deviceChartSpecs = [
    { key: "deviceUsers", title: "Users by Device", rows: deviceRows, metricKey: "users", formatter: formatGaNumber },
    { key: "deviceSessions", title: "Sessions by Device", rows: deviceRows, metricKey: "sessions", formatter: formatGaNumber },
    {
      key: "deviceEngagement",
      title: "Engagement Rate by Device",
      rows: deviceRows,
      metricKey: "engagementRate",
      formatter: formatGaPercent,
    },
  ].filter((spec) =>
    spec.rows.some((row) => Number.isFinite(row?.[spec.metricKey]) && row[spec.metricKey] > 0)
  );

  const siteActivitySpecs = [
    { key: "eventCount", title: "Event Count by Age Group", metricKey: "eventCount", formatter: formatGaNumber },
    {
      key: "avgSessionDuration",
      title: "Avg Session Duration by Age Group",
      metricKey: "avgSessionDuration",
      formatter: formatGaDuration,
    },
    {
      key: "eventsPerUser",
      title: "Events per User by Age Group",
      metricKey: "eventsPerUser",
      formatter: (value) => Number(value).toFixed(2),
    },
    {
      key: "sessionsPerUser",
      title: "Sessions per User by Age Group",
      metricKey: "sessionsPerUser",
      formatter: (value) => Number(value).toFixed(2),
    },
  ].filter((spec) =>
    ageRows.some((row) => Number.isFinite(row?.[spec.metricKey]) && row[spec.metricKey] > 0)
  );

  return {
    /*
     * GA4's own window figures travel with the breakdowns so a segmented card
     * can show the canonical aggregate instead of recomposing one from rows
     * that Google may have thresholded. Segment recomposition stays a
     * fallback and a cross-check, never a replacement.
     */
    windowMetrics: resolveGaWindowMetrics(channelData),
    ageRows,
    genderRows,
    deviceRows,
    ageChartSpecs,
    genderChartSpecs,
    deviceChartSpecs,
    siteActivitySpecs,
    demographicsStatus: channelData?.ga_demographics_status || channelData?.gaDemographicsStatus || null,
    hasAgeBreakdown: ageRows.length > 0,
    hasGenderBreakdown: genderRows.length > 0,
    hasDeviceBreakdown: deviceRows.length > 0,
    hasAnyBreakdown:
      ageRows.length > 0 || genderChartSpecs.length > 0 || deviceChartSpecs.length > 0,
  };
};

const MATERIAL_COVERAGE_TOLERANCE = 0.01;

/*
 * The presentation contract for one segmented GA4 card.
 *
 * Pure and exported so Cortex and Quantum are pinned to the same numbers by
 * test rather than by two people reading the same JSX carefully. Nothing in
 * here formats or renders — it decides WHAT the card is allowed to claim:
 * what the centre value is and on what authority, what the wedges are a
 * composition OF, and what has to be disclosed when the two do not line up.
 */
export const buildGaSegmentCardModel = ({
  title,
  rows,
  metricKey,
  formatter = formatGaNumber,
  windowMetrics = null,
}) => {
  const semantics = resolveGaSegmentMetricSemantics(metricKey);
  const sourceRows = Array.isArray(rows) ? rows : [];

  const segments = sourceRows
    .map((row, index) => ({
      label: row?.label,
      value: toNumber(row?.[metricKey]),
      weight: toNumber(row?.[semantics.compositionKey]),
      numerator: semantics.ratio ? toNumber(row?.[semantics.ratio.numeratorKey]) : NaN,
      denominator: semantics.ratio ? toNumber(row?.[semantics.ratio.denominatorKey]) : NaN,
      color: GA_DIMENSION_COLORS[index % GA_DIMENSION_COLORS.length],
    }))
    .filter((item) => item.label && Number.isFinite(item.value) && item.value > 0);

  if (!segments.length) return null;

  const segmentSum = segments.reduce((sum, item) => sum + item.value, 0);
  const compositionTotal = segments.reduce(
    (sum, item) => sum + (Number.isFinite(item.weight) && item.weight > 0 ? item.weight : 0),
    0
  );
  const hasComposition = compositionTotal > 0;
  const windowValue =
    windowMetrics && Number.isFinite(windowMetrics[metricKey]) ? windowMetrics[metricKey] : null;

  /*
   * A rate recomposed from the rows. Preferred order inside the fallback is
   * the exact ratio (Σ engaged sessions ÷ Σ sessions) over a weighted mean of
   * per-row rates, because the rows' rates are already rounded. Neither is
   * ever an arithmetic sum, and neither is a simple average.
   */
  const recomposeRatio = () => {
    if (!semantics.ratio) return null;
    const usable = segments.filter(
      (item) =>
        Number.isFinite(item.numerator) &&
        Number.isFinite(item.denominator) &&
        item.denominator > 0
    );
    if (usable.length !== segments.length) return null;
    const numerator = usable.reduce((sum, item) => sum + item.numerator, 0);
    const denominator = usable.reduce((sum, item) => sum + item.denominator, 0);
    if (!(denominator > 0)) return null;
    const ratio = numerator / denominator;
    return semantics.ratio.asPercent ? ratio * 100 : ratio;
  };

  const recomposeWeighted = () => {
    const usable = segments.filter((item) => Number.isFinite(item.weight) && item.weight > 0);
    if (!usable.length) return null;
    const weightTotal = usable.reduce((sum, item) => sum + item.weight, 0);
    if (!(weightTotal > 0)) return null;
    return usable.reduce((sum, item) => sum + item.value * item.weight, 0) / weightTotal;
  };

  let center = { value: null, basis: "unavailable" };
  if (semantics.aggregation === GA_SEGMENT_AGGREGATION.ADDITIVE) {
    center =
      windowValue != null
        ? { value: windowValue, basis: "window" }
        : { value: segmentSum, basis: "segment_sum" };
  } else if (semantics.aggregation === GA_SEGMENT_AGGREGATION.DISTINCT) {
    // Never the sum: a person counted in two segments is still one user.
    center = windowValue != null ? { value: windowValue, basis: "window" } : center;
  } else if (semantics.aggregation === GA_SEGMENT_AGGREGATION.WEIGHTED) {
    const ratioValue = recomposeRatio();
    const weightedValue = recomposeWeighted();
    if (windowValue != null) center = { value: windowValue, basis: "window" };
    else if (ratioValue != null) center = { value: ratioValue, basis: "recomposed_ratio" };
    else if (weightedValue != null) center = { value: weightedValue, basis: "weighted" };
    center.recomposed = ratioValue != null ? ratioValue : weightedValue;
  }

  /*
   * Bars are the honest fallback when the wedges would have to be faked: a
   * rate with no denominator to weigh it by, or a metric nobody classified.
   * Correctness outranks keeping the donut shape.
   */
  const compositionIsIndependent = semantics.compositionKey !== metricKey;
  const presentation =
    semantics.aggregation === GA_SEGMENT_AGGREGATION.UNSUPPORTED || !hasComposition
      ? "bars"
      : "donut";

  const disclosures = [];
  if (presentation === "donut" && compositionIsIndependent) {
    disclosures.push(
      `Wedges show ${semantics.compositionLabel} by segment. ${semantics.label} is not additive, so the centre is GA4's window figure — not the sum of the segment ${semantics.segmentNoun}.`
    );
  }
  if (semantics.aggregation === GA_SEGMENT_AGGREGATION.DISTINCT) {
    disclosures.push(
      `GA4 counts a ${semantics.label.replace(/s$/, "").toLowerCase()} once per segment, so the segments can total more than the window figure in the centre.`
    );
  }
  if (
    center.basis === "window" &&
    semantics.aggregation === GA_SEGMENT_AGGREGATION.ADDITIVE &&
    center.value > 0 &&
    Math.abs(segmentSum - center.value) / center.value > MATERIAL_COVERAGE_TOLERANCE
  ) {
    disclosures.push(
      `Segments cover ${((segmentSum / center.value) * 100).toFixed(1)}% of GA4's window ${semantics.label}; the rest is in rows GA4 did not break out.`
    );
  }
  if (
    center.basis === "window" &&
    semantics.aggregation === GA_SEGMENT_AGGREGATION.WEIGHTED &&
    center.recomposed != null &&
    center.value > 0 &&
    Math.abs(center.recomposed - center.value) / center.value > MATERIAL_COVERAGE_TOLERANCE
  ) {
    disclosures.push(
      `The segment rows recompose to ${formatter(center.recomposed)} against GA4's ${formatter(
        center.value
      )} for the window. GA4's window figure is shown; the gap is segment coverage, not a correction.`
    );
  }
  if (center.basis === "unavailable") {
    disclosures.push(
      `GA4 did not report a window ${semantics.label} and the rows carry no ${semantics.compositionLabel} to weigh them by, so no combined figure is shown. The segment values below are each segment's own measurement.`
    );
  }

  return {
    title,
    metricKey,
    formatter,
    presentation,
    aggregation: semantics.aggregation,
    metricLabel: semantics.label,
    compositionKey: semantics.compositionKey,
    compositionLabel: semantics.compositionLabel,
    compositionIsIndependent,
    compositionTotal,
    segmentSum,
    center,
    disclosures,
    segments: segments.map((item) => ({
      label: item.label,
      color: item.color,
      value: item.value,
      weight: Number.isFinite(item.weight) && item.weight > 0 ? item.weight : 0,
      share:
        compositionTotal > 0 && Number.isFinite(item.weight) && item.weight > 0
          ? item.weight / compositionTotal
          : 0,
    })),
  };
};

const describeGaCenterBasis = (model) => {
  if (model.center.basis === "window") return `GA4 window ${model.metricLabel}`;
  if (model.center.basis === "segment_sum") return `Total ${model.metricLabel}`;
  if (model.center.basis === "recomposed_ratio" || model.center.basis === "weighted")
    return `${model.metricLabel}, weighted by ${model.compositionLabel}`;
  return `${model.metricLabel} unavailable`;
};

const renderGaSegmentLegend = (model) => (
  <div className="sherlocreports-ga-chart-legend">
    {model.segments.map((item) => (
      <div
        key={`${model.title}-${item.label}`}
        className="sherlocreports-ga-legend-row"
        title={
          model.compositionIsIndependent
            ? `${item.label} · ${model.metricLabel} ${model.formatter(
                item.value
              )} · ${formatGaNumber(item.weight)} ${model.compositionLabel} (${(
                item.share * 100
              ).toFixed(1)}% of the total)`
            : `${item.label} · ${model.formatter(item.value)}`
        }
      >
        <span
          className="sherlocreports-ga-legend-swatch"
          style={{ backgroundColor: item.color }}
        />
        <span className="sherlocreports-ga-legend-label">{item.label}</span>
        <strong>{model.formatter(item.value)}</strong>
        {model.compositionIsIndependent ? (
          <span className="sherlocreports-ga-legend-share sherlocreports-numeric">
            {`${(item.share * 100).toFixed(1)}% ${model.compositionLabel.toLowerCase()}`}
          </span>
        ) : null}
      </div>
    ))}
  </div>
);

const renderGaSegmentDisclosures = (model) =>
  model.disclosures.map((text) => (
    <p
      className="sherlocreports-ga-demographic-helper sherlocreports-ga-chart-note"
      key={`${model.title}-${model.metricKey}-${text.slice(0, 24)}`}
    >
      {text}
    </p>
  ));

const renderGaSegmentBars = (model) => {
  const maxValue = Math.max(...model.segments.map((item) => item.value), 0);
  if (!(maxValue > 0)) return null;
  return (
    <div className="sherlocreports-ga-bar-list">
      {model.segments.map((item) => (
        <div key={`${model.title}-${item.label}`} className="sherlocreports-ga-bar-row">
          <span>{item.label}</span>
          <div className="sherlocreports-ga-bar-track">
            <div
              className="sherlocreports-ga-bar-fill"
              style={{
                width: `${Math.max(4, (item.value / maxValue) * 100)}%`,
                backgroundColor: item.color,
              }}
            />
          </div>
          <strong>{model.formatter(item.value)}</strong>
        </div>
      ))}
    </div>
  );
};

/*
 * Kept under its shipped name and signature: every existing call site passes a
 * spec through it. What changed is that it now renders a decided model rather
 * than deciding "total = Σ segment values" inline for whatever it was handed.
 */
export const renderGaDonutCard = ({
  title,
  rows,
  metricKey,
  formatter = formatGaNumber,
  windowMetrics = null,
}) => {
  const model = buildGaSegmentCardModel({ title, rows, metricKey, formatter, windowMetrics });
  if (!model) return null;

  if (model.presentation === "bars") {
    return (
      <div className="sherlocreports-ga-chart-card" key={`${title}-${metricKey}`}>
        <h4>{title}</h4>
        {model.center.basis === "window" ? (
          <p className="sherlocreports-ga-chart-aggregate sherlocreports-numeric">
            {`${describeGaCenterBasis(model)}: ${formatter(model.center.value)}`}
          </p>
        ) : null}
        {renderGaSegmentBars(model)}
        {renderGaSegmentDisclosures(model)}
      </div>
    );
  }

  let cursor = 0;
  const gradient = model.segments
    .map((item) => {
      const start = cursor;
      const end = start + item.share * 100;
      cursor = end;
      return `${item.color} ${start}% ${end}%`;
    })
    .join(", ");

  const centerText =
    model.center.basis === "unavailable" ? "—" : formatter(model.center.value);

  return (
    <div className="sherlocreports-ga-chart-card" key={`${title}-${metricKey}`}>
      <h4>{title}</h4>
      <div className="sherlocreports-ga-donut-layout">
        <div className="sherlocreports-ga-donut-figure">
          <div
            className="sherlocreports-ga-donut"
            style={{ background: `conic-gradient(${gradient})` }}
            aria-label={`${title}. ${describeGaCenterBasis(model)}: ${centerText}. Wedges are ${
              model.compositionLabel
            } by segment.`}
          >
            <span>{centerText}</span>
          </div>
          {/*
            The basis sits UNDER the ring, not inside it. What the centre
            number is measuring is a whole phrase — "GA4 window Engagement
            Rate" — and a 76px hole cannot hold it legibly.
          */}
          <span className="sherlocreports-ga-donut-basis">{describeGaCenterBasis(model)}</span>
        </div>
        {renderGaSegmentLegend(model)}
      </div>
      {renderGaSegmentDisclosures(model)}
    </div>
  );
};

/*
 * Bars make no part-to-whole claim, so they were never wrong — but they read
 * the same declared semantics so that a card which states an aggregate states
 * a VALID one (Avg Session Duration by Age Group is weighted by sessions, not
 * added up), and so both card shapes share one bar implementation.
 */
export const renderGaBarCard = ({
  title,
  rows,
  metricKey,
  formatter = formatGaNumber,
  windowMetrics = null,
}) => {
  const model = buildGaSegmentCardModel({ title, rows, metricKey, formatter, windowMetrics });
  if (!model) return null;
  const bars = renderGaSegmentBars(model);
  if (!bars) return null;

  return (
    <div className="sherlocreports-ga-chart-card" key={`${title}-${metricKey}`}>
      <h4>{title}</h4>
      {/*
        Only GA4's own window figure is stated above a bar list. A bar chart
        makes no part-to-whole claim, so it has no business asserting a
        derived combined value the reader cannot see the basis of.
      */}
      {model.center.basis === "window" ? (
        <p className="sherlocreports-ga-chart-aggregate sherlocreports-numeric">
          {`${describeGaCenterBasis(model)}: ${formatter(model.center.value)}`}
        </p>
      ) : null}
      {bars}
      {renderGaSegmentDisclosures(model)}
    </div>
  );
};

export const renderGoogleAnalyticsAudienceDashboard = (dashboard) => {
  if (!dashboard) return null;

  if (!dashboard.hasAnyBreakdown) {
    return null;
  }

  const hasAudienceCharts =
    dashboard.ageChartSpecs.length ||
    dashboard.genderChartSpecs.length ||
    dashboard.deviceChartSpecs.length;
  const hasAgeGenderCharts =
    dashboard.ageChartSpecs.length || dashboard.genderChartSpecs.length;
  const showAgeGenderPendingNote =
    dashboard.deviceChartSpecs.length > 0 && !hasAgeGenderCharts;

  return (
    <div className="sherlocreports-ga-detail-dashboard">
      {hasAudienceCharts ? (
        <section className="sherlocreports-ga-dashboard-section">
          <h3>Audience Breakdown</h3>
          {showAgeGenderPendingNote ? (
            <p className="sherlocreports-ga-demographic-helper">
              Google is withholding age and gender rows for privacy — the demographic query
              is subject to Google&apos;s thresholding. They appear once eligible traffic
              clears that threshold. Device breakdowns below are unaffected.
            </p>
          ) : null}
          <div className="sherlocreports-ga-chart-grid">
            {dashboard.ageChartSpecs.map((spec) =>
              renderGaDonutCard({
                ...spec,
                rows: dashboard.ageRows,
                windowMetrics: dashboard.windowMetrics,
              })
            )}
            {dashboard.genderChartSpecs.map((spec) =>
              renderGaDonutCard({ ...spec, windowMetrics: dashboard.windowMetrics })
            )}
            {dashboard.deviceChartSpecs.map((spec) =>
              renderGaDonutCard({ ...spec, windowMetrics: dashboard.windowMetrics })
            )}
          </div>
        </section>
      ) : null}

      {dashboard.hasAgeBreakdown ? (
        <section className="sherlocreports-ga-dashboard-section">
          <h3>Engagement by Age Group</h3>
          <div className="sherlocreports-ga-age-table-wrap">
            <table className="sherlocreports-ga-age-table">
              <thead>
                <tr>
                  <th>Age Range</th>
                  <th>Users</th>
                  <th>Sessions</th>
                  <th>Engaged Sessions</th>
                  <th>Engagement Rate</th>
                  <th>Event Count</th>
                  <th>Avg Session Duration</th>
                  <th>Events per User</th>
                  <th>Sessions per User</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.ageRows.map((row) => (
                  <tr key={`ga-age-row-${row.label}`}>
                    <td>{row.label}</td>
                    <td>{row.users > 0 ? formatGaNumber(row.users) : "—"}</td>
                    <td>{row.sessions > 0 ? formatGaNumber(row.sessions) : "—"}</td>
                    <td>{row.engagedSessions > 0 ? formatGaNumber(row.engagedSessions) : "—"}</td>
                    <td>{row.engagementRate > 0 ? formatGaPercent(row.engagementRate) : "—"}</td>
                    <td>{row.eventCount > 0 ? formatGaNumber(row.eventCount) : "—"}</td>
                    <td>{row.avgSessionDuration > 0 ? formatGaDuration(row.avgSessionDuration) : "—"}</td>
                    <td>{row.eventsPerUser ? row.eventsPerUser.toFixed(2) : "—"}</td>
                    <td>{row.sessionsPerUser ? row.sessionsPerUser.toFixed(2) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {dashboard.siteActivitySpecs.length ? (
        <section className="sherlocreports-ga-dashboard-section">
          <h3>Site Activity by Age Group</h3>
          <div className="sherlocreports-ga-chart-grid">
            {dashboard.siteActivitySpecs.map((spec) =>
              renderGaBarCard({
                ...spec,
                rows: dashboard.ageRows,
                windowMetrics: dashboard.windowMetrics,
              })
            )}
          </div>
        </section>
      ) : null}
    </div>
  );
};
